import { useCallback, useEffect, useRef, useState } from 'react';

import { CsAsset } from '../../../common/models/content-studio.model';
import { applyBoardOps, BoardOp, newShotId } from './board-ops';
import { clipIn3d, needsCapture } from './surface-shots';
import { emptyBoard, isEmptyBoard, isTakeShot, StudioBoard, StudioShot, ShotStageState, stillSpecKey } from './studio-contract';
import { StudioPrices, useStudioApi } from './use-studio-api';

export interface ProductionState {
    loading: boolean;
    error: string | null;
    projectName: string;
    board: StudioBoard;
    balance: number | null;
    prices: StudioPrices | null;
}

const SAVE_DEBOUNCE_MS = 800;
const POLL_MS = 3000;

function isPending(s?: ShotStageState) {
    return s?.status === 'pending';
}

/**
 * One production: its board, saved as it changes, and the pictures and films
 * being made for it.
 *
 * Generation is fire-and-watch: starting a still or clip writes a pending
 * stage onto the shot with the asset's uuid, and a single poll of the
 * project's assets settles every pending stage at once — so a board of eight
 * shots costs one request every few seconds, not eight.
 */
export function useProduction(projectUuid: string | undefined) {
    const api = useStudioApi();
    const [state, setState] = useState<ProductionState>({
        loading: true, error: null, projectName: '', board: emptyBoard(), balance: null, prices: null,
    });
    const boardRef = useRef<StudioBoard>(state.board);
    const saveTimer = useRef<number | null>(null);
    const loadedRef = useRef(false);
    // Set once the production is deleted, so leaving does not save it back.
    const deletedRef = useRef(false);
    // Set once the vendor's first request is under way: the production is
    // theirs from then on, even while its reference is still uploading and the
    // board is empty. Without it, a remount in that window deleted it (13 Oct 2026).
    const keepRef = useRef(false);
    const keep = useCallback(() => { keepRef.current = true; }, []);

    const persist = useCallback((board: StudioBoard, name?: string) => {
        if (!projectUuid) return;
        if (saveTimer.current) window.clearTimeout(saveTimer.current);
        saveTimer.current = window.setTimeout(() => {
            api.updateProject(projectUuid, { board, ...(name ? { name } : {}) } as any).catch(() => undefined);
        }, SAVE_DEBOUNCE_MS);
    }, [api, projectUuid]);

    const setBoard = useCallback((updater: (b: StudioBoard) => StudioBoard) => {
        setState((s) => {
            const next = updater(s.board);
            boardRef.current = next;
            if (loadedRef.current) persist(next);
            return { ...s, board: next };
        });
    }, [persist]);

    const refreshBalance = useCallback(async () => {
        try {
            const { balance } = await api.getCreditBalance();
            setState((s) => ({ ...s, balance }));
        } catch { /* the badge shows a dash */ }
    }, [api]);

    // ── load ──────────────────────────────────────────────────
    useEffect(() => {
        if (!projectUuid) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const project: any = await api.getProject(projectUuid);
                if (cancelled) return;
                const board: StudioBoard = project?.board?.version ? project.board : { ...emptyBoard(), brief: project?.description || undefined };
                boardRef.current = board;
                loadedRef.current = true;
                setState((s) => ({ ...s, loading: false, projectName: project?.name || '', board }));
            } catch (err: any) {
                if (!cancelled) setState((s) => ({ ...s, loading: false, error: err?.message || 'Could not open this production' }));
            }
        })();
        refreshBalance();
        api.getPrices().then((prices) => !cancelled && setState((s) => ({ ...s, prices }))).catch(() => undefined);
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectUuid]);

    // Leaving: a production nothing was done in is not kept (it would sit in
    // the list as "New production · 0 shots"); otherwise flush a pending save.
    useEffect(() => () => {
        if (!projectUuid || deletedRef.current) return;
        if (loadedRef.current && isEmptyBoard(boardRef.current) && !keepRef.current && !boardRef.current.brief) {
            if (saveTimer.current) window.clearTimeout(saveTimer.current);
            api.deleteProject(projectUuid).catch(() => undefined);
            return;
        }
        if (saveTimer.current) {
            window.clearTimeout(saveTimer.current);
            api.updateProject(projectUuid, { board: boardRef.current } as any).catch(() => undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectUuid]);

    /** Delete the whole production: shots, photos, clips, the film. */
    const remove = useCallback(async () => {
        if (!projectUuid) return;
        if (saveTimer.current) window.clearTimeout(saveTimer.current);
        await api.deleteProject(projectUuid);
        deletedRef.current = true;
    }, [api, projectUuid]);

    const rename = useCallback((name: string) => {
        setState((s) => ({ ...s, projectName: name }));
        if (projectUuid) api.updateProject(projectUuid, { name } as any).catch(() => undefined);
    }, [api, projectUuid]);

    const updateShot = useCallback((id: string, patch: Partial<StudioShot>) => {
        setBoard((b) => applyBoardOps(b, [{ op: 'update_shot', id, patch }]).board);
    }, [setBoard]);

    const setStage = useCallback((id: string, stage: 'still' | 'clip', value: ShotStageState) => {
        setBoard((b) => ({ ...b, shots: b.shots.map((s) => (s.id === id ? { ...s, [stage]: value } : s)) }));
    }, [setBoard]);

    /**
     * Apply Ardi's changes now, synchronously, and say which shots they touched
     * — the caller renders those next, and a state updater would only run on
     * the next render, after it had asked.
     */
    const applyOps = useCallback((ops: BoardOp[]) => {
        // New shots get their ids here, so applying the same ops to the
        // latest state below gives the same shots as this synchronous pass.
        const fixed = (ops || []).map((op) => (op.op === 'add_shot' && op.shot && !op.shot.id ? { ...op, shot: { ...op.shot, id: newShotId() } } : op));
        const res = applyBoardOps(boardRef.current, fixed);
        // Applied as an updater, not as a value: a chat line queued a moment
        // ago (Ardi's reply) must survive, and replacing the board would drop it.
        setBoard((b) => applyBoardOps(b, fixed).board);
        boardRef.current = res.board;
        return res.changed;
    }, [setBoard]);

    // ── generation ────────────────────────────────────────────
    const makeStill = useCallback(async (shotId: string) => {
        const shot = boardRef.current.shots.find((s) => s.id === shotId);
        if (!shot || !projectUuid || shot.kind === 'card') return;
        if (!shot.productUuids.length) {
            setStage(shotId, 'still', { ...shot.still, status: 'failed', error: 'Pick a product for this shot first' });
            return;
        }
        const previous = shot.still?.status === 'ready' && shot.still.assetUuid && shot.still.url
            ? [{ assetUuid: shot.still.assetUuid, url: shot.still.url }, ...(shot.still.takes || [])].slice(0, 8)
            : shot.still?.takes;
        // A tile shot as an object is built in 3D first: the board's capture
        // runner poses the real pieces, and the photo is made from that.
        if (needsCapture(boardRef.current, shot)) {
            setStage(shotId, 'still', { status: 'waiting', waitingFor: 'capture', takes: previous, url: shot.still?.url });
            return;
        }
        // Made from a key photo that is not there yet: the key photo first,
        // this one as soon as it is in.
        const key = shot.basedOn ? boardRef.current.shots.find((x) => x.id === shot.basedOn) : undefined;
        if (key && key.still?.status !== 'ready') {
            if (key.still?.status !== 'pending' && key.still?.status !== 'waiting') void makeStillRef.current?.(key.id);
            setStage(shotId, 'still', { status: 'waiting', waitingFor: 'key', takes: previous, url: shot.still?.url });
            return;
        }
        const specKey = stillSpecKey(boardRef.current, shot);
        // The film was made from the photo being replaced: it goes into the
        // film's history, so the card shows the new photo and offers to film
        // it, instead of an old film that hides the new picture.
        if (shot.clip?.status === 'ready' && shot.clip.assetUuid && shot.clip.url) {
            setStage(shotId, 'clip', { takes: [{ assetUuid: shot.clip.assetUuid, url: shot.clip.url }, ...(shot.clip.takes || [])].slice(0, 8) });
        }
        setStage(shotId, 'still', { status: 'pending', takes: previous, url: shot.still?.url, specKey });
        try {
            const assetUuid = await api.makeStill(projectUuid, boardRef.current, shot);
            setStage(shotId, 'still', { status: 'pending', assetUuid, takes: previous, url: shot.still?.url, specKey });
            refreshBalance();
        } catch (err: any) {
            setStage(shotId, 'still', { status: 'failed', error: readError(err), takes: previous, url: shot.still?.url });
        }
    }, [api, projectUuid, refreshBalance, setStage]);
    const makeStillRef = useRef<((id: string) => Promise<void>) | null>(null);
    makeStillRef.current = makeStill;

    // Shots waiting for their key photo start when it is in; if it failed, they say so.
    useEffect(() => {
        for (const s of state.board.shots) {
            if (s.still?.status !== 'waiting' || s.still.waitingFor === 'capture') continue;
            const key = state.board.shots.find((x) => x.id === s.basedOn);
            if (!key) { void makeStill(s.id); continue; }
            if (key.still?.status === 'ready') void makeStill(s.id);
            else if (key.still?.status === 'failed') setStage(s.id, 'still', { ...s.still, status: 'failed', error: 'The key photo this shot is made from did not work — make it again first.' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.board.shots]);

    /**
     * Edit a shot's photo — retouch, modify, expand, upscale, a new angle.
     *
     * The result is a new take of the same shot: the photo it was made from
     * goes into the history (and so does any film made from it), exactly as
     * with a new photo take. Nothing is lost; any take can be put back.
     */
    const applyEdit = useCallback(async (shotId: string, req: { recipeSlug: string; inputs: Record<string, any> }) => {
        const shot = boardRef.current.shots.find((s) => s.id === shotId);
        if (!shot || !projectUuid || shot.still?.status !== 'ready' || !shot.still.assetUuid || !shot.still.url) return;
        const previous = [{ assetUuid: shot.still.assetUuid, url: shot.still.url }, ...(shot.still.takes || [])].slice(0, 8);
        if (shot.clip?.status === 'ready' && shot.clip.assetUuid && shot.clip.url) {
            setStage(shotId, 'clip', { takes: [{ assetUuid: shot.clip.assetUuid, url: shot.clip.url }, ...(shot.clip.takes || [])].slice(0, 8) });
        }
        const keep = { takes: previous, url: shot.still.url, specKey: shot.still.specKey };
        setStage(shotId, 'still', { ...keep, status: 'pending' });
        try {
            const res: any = await api.createGeneration({
                projectUuid,
                recipeSlug: req.recipeSlug,
                inputs: req.inputs,
                // The angle tool reads the product; the edit tools only the picture.
                sourceProductUuids: req.recipeSlug === 'style-variation' ? shot.productUuids : [],
                parentAssetUuid: shot.still.assetUuid,
                count: 1,
            } as any);
            const assetUuid = res?.assets?.[0]?.uuid;
            if (!assetUuid) throw new Error('The edit could not be started');
            setStage(shotId, 'still', { ...keep, status: 'pending', assetUuid });
            refreshBalance();
        } catch (err: any) {
            // The photo is untouched: put it back, with the reason on it.
            setStage(shotId, 'still', { ...shot.still, error: readError(err) });
        }
    }, [api, projectUuid, refreshBalance, setStage]);

    /**
     * A finished picture made in the browser (a Design export) becomes the
     * shot's photo; the one it replaces goes into the history, as with edits.
     */
    const addVersion = useCallback((shotId: string, asset: { uuid: string; url: string }, patch: Partial<StudioShot> = {}) => {
        setBoard((b) => ({
            ...b,
            shots: b.shots.map((s) => {
                if (s.id !== shotId) return s;
                const takes = s.still?.assetUuid && s.still.url
                    ? [{ assetUuid: s.still.assetUuid, url: s.still.url }, ...(s.still.takes || [])].slice(0, 8)
                    : s.still?.takes;
                const clip = s.clip?.status === 'ready' && s.clip.assetUuid && s.clip.url
                    ? { takes: [{ assetUuid: s.clip.assetUuid, url: s.clip.url }, ...(s.clip.takes || [])].slice(0, 8) }
                    : s.clip;
                return { ...s, ...patch, clip, still: { status: 'ready', assetUuid: asset.uuid, url: asset.url, takes, specKey: s.still?.specKey } };
            }),
        }));
    }, [setBoard]);

    const makeClip = useCallback(async (shotId: string, patch?: Partial<StudioShot>) => {
        const found = boardRef.current.shots.find((s) => s.id === shotId);
        // The patch is the change the caller just made, before the board has re-rendered with it.
        const shot = found && patch ? { ...found, ...patch } : found;
        if (!shot || !projectUuid || shot.kind !== 'video') return;
        const previous = shot.clip?.status === 'ready' && shot.clip.assetUuid && shot.clip.url
            ? [{ assetUuid: shot.clip.assetUuid, url: shot.clip.url }, ...(shot.clip.takes || [])].slice(0, 8)
            : shot.clip?.takes;
        if (clipIn3d(shot) && !isTakeShot(shot)) {
            // Rendered by the 3D studio on this page (see ProductionPage).
            setStage(shotId, 'clip', { status: 'waiting', waitingFor: 'capture', takes: previous });
            return;
        }
        setStage(shotId, 'clip', { status: 'pending', takes: previous });
        try {
            const assetUuid = await api.makeClip(projectUuid, boardRef.current, shot);
            setStage(shotId, 'clip', { status: 'pending', assetUuid, takes: previous });
            refreshBalance();
        } catch (err: any) {
            setStage(shotId, 'clip', { status: 'failed', error: readError(err), takes: previous });
        }
    }, [api, projectUuid, refreshBalance, setStage]);

    /**
     * A tile filmed in 3D, made real: its 3D clip becomes the shot's take and
     * H3 films it for real, camera move kept. For references shot as real
     * footage (handheld, on a phone), where clean renders never looked like
     * them (14 Oct 2026).
     */
    const makeReal = useCallback(async (shotId: string) => {
        const shot = boardRef.current.shots.find((s) => s.id === shotId);
        if (!shot?.clip?.url || shot.clip.status !== 'ready') return;
        const patch: Partial<StudioShot> = {
            engine: 'take',
            take3d: {
                url: shot.clip.url,
                posterUrl: shot.still?.url,
                durationSec: shot.clip.durationSec || shot.durationSec || 4,
                source: 'keyframes',
            },
        };
        updateShot(shotId, patch);
        await makeClip(shotId, patch);
    }, [makeClip, updateShot]);

    /**
     * Put an earlier take back on the card. Nothing is ever thrown away: the
     * take shown now goes into the history in its place.
     */
    const useTake = useCallback((shotId: string, stage: 'still' | 'clip', assetUuid: string) => {
        setBoard((b) => ({
            ...b,
            shots: b.shots.map((s) => {
                const st = s[stage];
                const pick = st?.takes?.find((x) => x.assetUuid === assetUuid);
                if (s.id !== shotId || !st || !pick) return s;
                const rest = (st.takes || []).filter((x) => x.assetUuid !== assetUuid);
                const current = st.assetUuid && st.url ? [{ assetUuid: st.assetUuid, url: st.url }] : [];
                return { ...s, [stage]: { ...st, status: 'ready', error: undefined, assetUuid: pick.assetUuid, url: pick.url, takes: [...current, ...rest] } };
            }),
        }));
    }, [setBoard]);

    // ── poll pending stages ───────────────────────────────────
    // The loop reads the API client and the board setter through refs, so a
    // re-render never restarts it: restarted faster than its first tick, it
    // never ran, and finished clips sat "filming" for 25 minutes (13 Oct 2026).
    const anyPending = state.board.shots.some((s) => isPending(s.still) || isPending(s.clip));
    const pollDeps = useRef({ api, setBoard, refreshBalance });
    pollDeps.current = { api, setBoard, refreshBalance };
    useEffect(() => {
        if (!anyPending || !projectUuid) return undefined;
        let stop = false;
        const tick = async () => {
            const { api, setBoard, refreshBalance } = pollDeps.current;
            try {
                const res: any = await api.listAssets({ projectUuid, limit: 200 } as any);
                const items: CsAsset[] = res?.items || res?.assets || res?.data || res || [];
                const byUuid = new Map(items.map((a) => [a.uuid, a]));
                // Only touch the board (and so save it) when something settled.
                const settles = boardRef.current.shots.some((s) => (['still', 'clip'] as const).some((stage) => {
                    const st = s[stage];
                    const a: any = st?.assetUuid ? byUuid.get(st.assetUuid) : undefined;
                    return isPending(st) && a && a.status !== 'pending';
                }));
                if (settles) setBoard((b) => ({
                    ...b,
                    shots: b.shots.map((s) => {
                        let next = s;
                        for (const stage of ['still', 'clip'] as const) {
                            const st = next[stage];
                            if (!isPending(st) || !st?.assetUuid) continue;
                            const a: any = byUuid.get(st.assetUuid);
                            if (!a || a.status === 'pending') continue;
                            next = {
                                ...next,
                                [stage]: a.status === 'ready'
                                    ? { ...st, status: 'ready', url: a.url, error: undefined, durationSec: a.dimensions?.durationSec }
                                    // An edit that failed leaves the photo it started from in place.
                                    : st.takes?.[0] && st.url === st.takes[0].url
                                        ? { ...st, status: 'ready', assetUuid: st.takes[0].assetUuid, takes: st.takes.slice(1), error: a.error || 'The edit did not work this time — try again' }
                                        : { ...st, status: 'failed', error: a.error || 'It did not work this time — try again' },
                            };
                        }
                        return next;
                    }),
                }));
                if (settles) refreshBalance();
            } catch { /* next tick */ }
            if (!stop) window.setTimeout(tick, POLL_MS);
        };
        const first = window.setTimeout(tick, POLL_MS);
        return () => { stop = true; window.clearTimeout(first); };
    }, [anyPending, projectUuid]);

    return {
        ...state,
        boardRef,
        setBoard,
        applyOps,
        updateShot,
        makeReal,
        setStage,
        makeStill,
        makeClip,
        applyEdit,
        addVersion,
        useTake,
        rename,
        remove,
        refreshBalance,
        keep,
    };
}

export function readError(err: any): string {
    const m = err?.response?.data?.message || err?.message || '';
    if (/insufficient credits|not enough credits/i.test(m)) return 'Not enough credits';
    return String(m || 'Something went wrong').slice(0, 200);
}
